import {Component, Input, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {
    Account,
    NotificationRequest,
    NotificationResponse,
    RefundState,
    RefundView,
    WebRefundService
} from 'web-angular-client';
import {SyncValidators} from '../../validator/SyncValidators';
import {finalize} from 'rxjs';
import {extractServerErrorMessage} from '../../util/server-error-util';
import NotificationTypeEnum = NotificationRequest.NotificationTypeEnum;

/** Minimum refund amount in cents (R15.00); mirrors the engine {@code MIN_REFUND_AMOUNT_CENTS} bound. */
const MIN_REFUND_AMOUNT_CENTS = 1500;

/**
 * Two-step refund dialog: captures a (partial) refund amount plus an optional description (validated via the shared
 * {@code ValidationComponent}/reactive-form pattern), calls the refund API, then shows the result — the
 * customer-facing refund link on success, or the failure reason if ePay rejected it. The backend re-validates
 * eligibility (ecommerce account, refundable state, eligible payment network, amount bounds), so any rejection
 * surfaces here either inline (HTTP 4xx/5xx) or as a FAILED result (ePay business rejection, HTTP 201).
 */
@Component({
    selector: 'app-refund-dialog',
    templateUrl: './refund-dialog.component.html',
    styleUrls: ['./refund-dialog.component.scss'],
    standalone: false
})
export class RefundDialogComponent implements OnInit
{
    @Input() account: Account;

    view: 'form' | 'result' = 'form';
    form: FormGroup<{ amount: FormControl<number | null>; description: FormControl<string | null> }>;
    submitting = false;
    cancelling = false;
    errorMessage: string | null = null;
    result: RefundView | null = null;

    sendForm: FormGroup<{ email: FormControl<string | null>; mobileNumber: FormControl<string | null> }>;
    sendingEmail = false;
    sendingSms = false;
    sendError: string | null = null;
    sentEmail: string | null = null;
    sentSms: string | null = null;

    readonly maxDescriptionLength = 255;
    readonly minAmountCents = MIN_REFUND_AMOUNT_CENTS;

    constructor(
        private readonly activeModal: NgbActiveModal,
        private readonly webRefundService: WebRefundService)
    {
    }

    get maxAmountCents(): number
    {
        return this.account?.amountPaid ?? this.account?.amount ?? 0;
    }

    get amountCents(): number
    {
        return Math.round((this.form?.controls.amount.value ?? 0) * 100);
    }

    get resultFailed(): boolean
    {
        return this.result?.refundState === RefundState.FAILED;
    }

    get emailAlreadySent(): boolean
    {
        return this.sentEmail != null && this.sentEmail === this.sendForm?.controls.email.value;
    }

    get smsAlreadySent(): boolean
    {
        return this.sentSms != null && this.sentSms === this.sendForm?.controls.mobileNumber.value;
    }

    ngOnInit(): void
    {
        this.form = new FormGroup({
            amount: new FormControl<number | null>(this.maxAmountCents / 100, [
                Validators.required,
                SyncValidators.refundAmountRange(this.minAmountCents, this.maxAmountCents)
            ]),
            description: new FormControl<string | null>('', [Validators.maxLength(this.maxDescriptionLength)])
        });

        this.sendForm = new FormGroup({
            email: new FormControl<string | null>(this.account?.customerEmail ?? '', [Validators.email]),
            mobileNumber: new FormControl<string | null>(
                this.account?.customerMobileNumber ?? '',
                [SyncValidators.validateMobileNumber()])
        });
    }

    submit(): void
    {
        this.errorMessage = null;
        const accountId = this.account?.id;
        if (accountId == null || this.submitting)
        {
            return;
        }
        if (this.form.invalid)
        {
            this.form.markAllAsTouched();
            return;
        }
        this.submitting = true;
        const description = this.form.controls.description.value?.trim();
        this.webRefundService
            .initiateRefund(accountId, {amount: this.amountCents, description: description || undefined})
            .subscribe({
                next: (refund) =>
                {
                    this.submitting = false;
                    this.result = refund;
                    this.view = 'result';
                },
                error: (err) =>
                {
                    this.submitting = false;
                    this.errorMessage = this.resolveCreateErrorMessage(err);
                }
            });
    }

    cancelRefund(): void
    {
        const refundId = this.result?.id;
        if (refundId == null || this.cancelling)
        {
            return;
        }
        this.cancelling = true;
        this.errorMessage = null;
        this.webRefundService.cancelRefund(refundId).subscribe({
            next: (refund) =>
            {
                this.cancelling = false;
                this.activeModal.close(refund);
            },
            error: (err) =>
            {
                this.cancelling = false;
                this.errorMessage = extractServerErrorMessage(err, 'The refund could not be cancelled.');
            }
        });
    }

    openLink(): void
    {
        const url = this.result?.refundUrl;
        if (!url)
        {
            return;
        }
        // 'noopener' prevents the opened refund page from gaining a reference back to this window.
        window.open(url, '_blank', 'noopener');
    }

    sendEmail(): void
    {
        const refundId = this.result?.id;
        if (refundId == null || !this.result?.refundUrl || this.sendingEmail || this.sendingSms)
        {
            return;
        }
        const control = this.sendForm.controls.email;
        if (control.invalid || !control.value)
        {
            control.markAsTouched();
            return;
        }
        const email = control.value;
        this.sendError = null;
        this.sendingEmail = true;
        const request: NotificationRequest = {
            notificationType: NotificationTypeEnum.EMAIL,
            initiatorType: 'MERCHANT',
            email
        };
        this.webRefundService
            .sendRefundNotification(refundId, request)
            .pipe(finalize(() => this.sendingEmail = false))
            .subscribe({
                next: (response) => this.handleSendResponse(response, 'email', email),
                error: (err) => this.sendError = extractServerErrorMessage(err, 'The refund link could not be sent.')
            });
    }

    sendSms(): void
    {
        const refundId = this.result?.id;
        if (refundId == null || !this.result?.refundUrl || this.sendingSms || this.sendingEmail)
        {
            return;
        }
        const control = this.sendForm.controls.mobileNumber;
        if (control.invalid || !control.value)
        {
            control.markAsTouched();
            return;
        }
        const mobileNumber = control.value;
        this.sendError = null;
        this.sendingSms = true;
        const request: NotificationRequest = {
            notificationType: NotificationTypeEnum.SMS,
            initiatorType: 'MERCHANT',
            mobileNumber
        };
        this.webRefundService
            .sendRefundNotification(refundId, request)
            .pipe(finalize(() => this.sendingSms = false))
            .subscribe({
                next: (response) => this.handleSendResponse(response, 'sms', mobileNumber),
                error: (err) => this.sendError = extractServerErrorMessage(err, 'The refund link could not be sent.')
            });
    }

    done(): void
    {
        this.activeModal.close(this.result ?? undefined);
    }

    dismiss(): void
    {
        this.activeModal.dismiss();
    }

    private handleSendResponse(response: NotificationResponse, channel: 'email' | 'sms', recipient: string | null): void
    {
        if (response.status === NotificationResponse.StatusEnum.SENT
            || response.status === NotificationResponse.StatusEnum.QUEUED)
        {
            if (channel === 'email')
            {
                this.sentEmail = recipient;
            }
            else
            {
                this.sentSms = recipient;
            }
            this.sendError = null;
        }
        else
        {
            this.sendError = response.responseMessage ?? 'The refund link could not be sent.';
        }
    }

    /**
     * Resolves the message shown when a refund create fails, mapping the two known backend conflicts to friendly copy
     * (most-specific first) and falling back to the generic backend/thrown message. The single-refund-per-payment
     * branch is temporary and will be removed once ePay can cater for multiple partial refunds.
     *
     * @param err the error caught from the refund create call
     * @returns the merchant-facing error message
     */
    private resolveCreateErrorMessage(err: any): string
    {
        if (this.alreadyHasRefund(err))
        {
            return 'A refund already exists for this payment. Only one refund is allowed per payment.';
        }
        if (this.exceedsRefundableBalance(err))
        {
            return 'Refund amount exceeds the remaining refundable balance for this transaction.';
        }
        return extractServerErrorMessage(err, 'The refund could not be created. Please try again.');
    }

    /**
     * TEMPORARY: detects the backend's single-refund-per-payment rejection so the dialog can show a friendly message
     * instead of the raw backend text. This (and its branch in resolveCreateErrorMessage) will be removed once ePay
     * can cater for multiple partial refunds.
     *
     * <p>Coupled to oltp's guard message (RefundService.validateNoExistingRefundLink), which carries "refund already
     * exists". That phrase is guarded by oltp RefundServiceTest, so a backend reword fails the build rather than
     * silently dropping this mapping to the generic fallback message.
     *
     * @param err the error caught from a refund API call
     * @returns {@code true} when the failure is the "payment already has a refund" conflict
     */
    private alreadyHasRefund(err: any): boolean
    {
        const raw = err?.error?.errorMessage ?? err?.error?.message ?? err?.message ?? '';
        return /refund already exists/i.test(raw);
    }

    /**
     * Detects the backend's over-refund rejection so the dialog can show a friendly "exceeds the remaining refundable
     * balance" message instead of the raw backend text.
     *
     * <p>Coupled to oltp's over-refund message (RefundService.validateRefundAmountWithinTransactionTotal), which
     * carries "exceeds transaction amount". That phrase is guarded by oltp RefundServiceTest, so a backend reword
     * fails the build rather than silently dropping this mapping to the generic fallback message.
     *
     * @param err the error caught from a refund API call
     * @returns {@code true} when the failure is the over-refund (amount-exceeds-balance) conflict
     */
    private exceedsRefundableBalance(err: any): boolean
    {
        const raw = err?.error?.errorMessage ?? err?.error?.message ?? err?.message ?? '';
        return /exceeds transaction amount/i.test(raw);
    }
}
